// Compte intervals.icu (preferences) et envoi des seances planifiees
// (IntervalsIcuController cote serveur).
import { avecAthlete, type Cible, type SeancePlanifiee } from './planningApi.ts';
import { versIntervalsIcu } from '../Construction/intervalsIcu.ts';
import { ajusterNotation } from '../Construction/ajustementRecup.ts';
import { type ZoneRow } from '../Construction/allureTable.ts';

export type ConfigIntervalsIcu = {
  athleteId: string | null;
  cleConfiguree: boolean;
  finCle: string | null;
};

function entetes(): HeadersInit {
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') };
}

async function verifier(response: Response, attendus: number[]): Promise<Response> {
  if (attendus.includes(response.status)) return response;
  let message = 'statut ' + response.status;
  if (response.status === 404) message = 'séance introuvable';
  try {
    const erreur = await response.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps vide ou non JSON
  }
  throw new Error(message);
}

export async function obtenirConfigIntervalsIcu(): Promise<ConfigIntervalsIcu> {
  return (await (await verifier(await fetch('/api/preference/intervals-icu', { headers: entetes() }), [200])).json()) as ConfigIntervalsIcu;
}

// Compte configure ou non : lu une fois, oublie quand la configuration change.
let compteConfigure: Promise<boolean> | null = null;
function oublierCompte() {
  compteConfigure = null;
}

/** Vrai si l'utilisateur a renseigne son compte intervals.icu (envoi automatique). */
export function compteIntervalsIcuConfigure(): Promise<boolean> {
  if (!compteConfigure) {
    compteConfigure = obtenirConfigIntervalsIcu()
      .then((c) => !!c.athleteId && c.cleConfiguree)
      .catch(() => {
        oublierCompte();
        return false;
      });
  }
  return compteConfigure;
}

export async function enregistrerConfigIntervalsIcu(athleteId: string, apiKey: string): Promise<ConfigIntervalsIcu> {
  const response = await verifier(
    await fetch('/api/preference/intervals-icu', { method: 'PUT', headers: entetes(), body: JSON.stringify({ athleteId, apiKey }) }),
    [200],
  );
  oublierCompte();
  return (await response.json()) as ConfigIntervalsIcu;
}

export async function supprimerConfigIntervalsIcu(): Promise<void> {
  oublierCompte();
  await verifier(await fetch('/api/preference/intervals-icu', { method: 'DELETE', headers: entetes() }), [204]);
}

/** Envoie (ou met a jour) une seance planifiee dans le calendrier intervals.icu. */
export async function envoyerVersIntervalsIcu(id: string, description: string, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/' + encodeURIComponent(id) + '/intervals-icu', athlete), {
      method: 'POST',
      headers: entetes(),
      body: JSON.stringify({ description }),
    }),
    [200],
  );
  return (await response.json()) as SeancePlanifiee;
}

/**
 * Texte intervals.icu d'une seance planifiee : corps de seance, avec
 * l'echauffement et la recuperation du modele en sections Warmup / Cooldown,
 * recuperations ajustees selon la preference de l'utilisateur.
 */
export function texteIntervalsIcu(
  p: SeancePlanifiee,
  vma: number,
  table: ZoneRow[],
  ajustementRecup: number,
): { texte?: string; erreur?: string } {
  // Repos ou note : envoye comme note de journee (la consigne pour une note).
  if (p.categorie === 'REPOS' || p.categorie === 'NOTE') return { texte: p.notes || p.nom };
  const a = (n?: string | null) => (n ? ajusterNotation(n, ajustementRecup) : undefined);
  const r = versIntervalsIcu(a(p.notationCorps ?? p.notation) ?? '', vma, table, {
    echauffement: a(p.notationEchauffement),
    retourCalme: a(p.notationRecuperation),
  });
  return r.erreur ? { erreur: r.erreur } : { texte: r.texte };
}

export type ResultatEnvoiAuto = {
  p: SeancePlanifiee;
  message?: { severity: 'success' | 'warning'; texte: string };
};

/**
 * Envoi automatique d'une seance qui vient d'etre ajoutee au calendrier, si
 * le compte intervals.icu est configure. N'echoue jamais : en cas de
 * probleme, la seance reste ajoutee et un avertissement est renvoye.
 */
export async function envoyerAutomatiquement(
  p: SeancePlanifiee,
  vma: number,
  table: ZoneRow[],
  ajustementRecup: number,
  // Calendrier d'un athlete (coach) : son compte intervals.icu est-il relie ?
  cible?: { athlete: string; icuConfigure: boolean } | null,
): Promise<ResultatEnvoiAuto> {
  if (cible ? !cible.icuConfigure : !(await compteIntervalsIcuConfigure())) return { p };
  const { texte, erreur } = texteIntervalsIcu(p, vma, table, ajustementRecup);
  if (!texte) {
    return { p, message: { severity: 'warning', texte: `« ${p.nom} » ajoutée, mais non envoyée vers intervals.icu : ${erreur ?? 'texte vide'}.` } };
  }
  try {
    const maj = await envoyerVersIntervalsIcu(p.id, texte, cible?.athlete);
    return { p: maj, message: { severity: 'success', texte: `« ${maj.nom} » ajoutée et envoyée vers intervals.icu.` } };
  } catch (e) {
    return { p, message: { severity: 'warning', texte: `« ${p.nom} » ajoutée, mais l'envoi vers intervals.icu a échoué : ${(e as Error).message}` } };
  }
}
